import React, { useState, useEffect, useCallback } from 'react';
import { X, FolderPlus } from 'lucide-react';
import { 
  getPartyAlbums, createPartyAlbum, updatePartyAlbum, deletePartyAlbum,
  getPartyAlbum, addPhotoToAlbum, removePhotoFromAlbum, uploadFile 
} from '../../services/api';
import { AlbumPhotoView } from './albums/AlbumPhotoView';
import { AlbumForm } from './albums/AlbumForm';
import { AlbumCard } from './albums/AlbumCard';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const PartyAlbumManager = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showAlbumForm, setShowAlbumForm] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [albumFormData, setAlbumFormData] = useState({
    name: '', description: '', date: '', cover_image: '', order: 0
  });

  const fetchAlbums = useCallback(async () => {
    setLoading(true);
    try { setAlbums((await getPartyAlbums()).data); } 
    catch (error) { console.error('Error fetching albums:', error); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAlbums(); }, [fetchAlbums]);

  const fetchAlbumDetails = async (albumId) => {
    setLoading(true);
    try { setSelectedAlbum((await getPartyAlbum(albumId)).data); }
    catch (error) { console.error('Error fetching album details:', error); }
    setLoading(false);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try { setAlbumFormData({ ...albumFormData, cover_image: (await uploadFile(file)).data.url }); }
    catch (error) { console.error('Error uploading cover:', error); alert('Failed to upload cover image'); }
    setUploading(false);
  };

  const handleAlbumSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAlbum) { await updatePartyAlbum(editingAlbum.id, albumFormData); }
      else { await createPartyAlbum(albumFormData); }
      fetchAlbums();
      resetAlbumForm();
    } catch (error) { console.error('Error saving album:', error); alert('Failed to save album'); }
  };

  const handleDeleteAlbum = async (albumId) => {
    if (window.confirm('Are you sure you want to delete this album and all its photos?')) {
      try {
        await deletePartyAlbum(albumId);
        fetchAlbums();
        if (selectedAlbum?.id === albumId) setSelectedAlbum(null);
      } catch (error) { console.error('Error deleting album:', error); }
    }
  };

  const handleEditAlbum = (album) => {
    setAlbumFormData({
      name: album.name, description: album.description || '',
      date: album.date, cover_image: album.cover_image || '', order: album.order
    });
    setEditingAlbum(album);
    setShowAlbumForm(true);
  };

  const resetAlbumForm = () => {
    setAlbumFormData({ name: '', description: '', date: '', cover_image: '', order: 0 });
    setEditingAlbum(null);
    setShowAlbumForm(false);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedAlbum) return;
    setUploading(true);
    try {
      for (const file of files) {
        const uploadResponse = await uploadFile(file);
        await addPhotoToAlbum(selectedAlbum.id, {
          url: uploadResponse.data.url, title: file.name.split('.')[0],
          order: selectedAlbum.photos?.length || 0
        });
      }
      fetchAlbumDetails(selectedAlbum.id);
    } catch (error) { console.error('Error uploading photos:', error); alert('Failed to upload some photos'); }
    setUploading(false);
  };

  const handleDeletePhoto = async (photoId) => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      try { await removePhotoFromAlbum(selectedAlbum.id, photoId); fetchAlbumDetails(selectedAlbum.id); }
      catch (error) { console.error('Error deleting photo:', error); }
    }
  };

  // Photo view
  if (selectedAlbum) {
    return (
      <AlbumPhotoView
        album={selectedAlbum} uploading={uploading} loading={loading}
        onBack={() => setSelectedAlbum(null)} onUpload={handlePhotoUpload}
        onDeletePhoto={handleDeletePhoto} formatDate={formatDate}
      />
    );
  }

  // Albums list
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-purple-500">Party Albums</h2>
        <button onClick={() => setShowAlbumForm(!showAlbumForm)}
          className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 flex items-center gap-2">
          {showAlbumForm ? <X size={20} /> : <FolderPlus size={20} />}
          {showAlbumForm ? 'Cancel' : 'New Album'}
        </button>
      </div>

      {showAlbumForm && (
        <AlbumForm
          formData={albumFormData} setFormData={setAlbumFormData}
          editingAlbum={editingAlbum} uploading={uploading}
          onSubmit={handleAlbumSubmit} onCoverUpload={handleCoverUpload} onCancel={resetAlbumForm}
        />
      )}

      {loading ? (
        <div className="text-center text-gray-400 py-8">Loading albums...</div>
      ) : albums.length === 0 ? (
        <div className="text-center text-gray-400 py-8">
          <FolderPlus size={48} className="mx-auto mb-4 opacity-50" />
          <p>No party albums yet.</p>
          <p className="text-sm mt-2">Click &quot;New Album&quot; to create one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {albums.map((album) => (
            <AlbumCard key={album.id} album={album} formatDate={formatDate}
              onSelect={() => fetchAlbumDetails(album.id)}
              onEdit={() => handleEditAlbum(album)}
              onDelete={() => handleDeleteAlbum(album.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PartyAlbumManager;
